import React, { useCallback, useMemo, useState } from 'react';
import { Link } from "react-router-dom";
import moment from 'moment';
import { LinearProgress } from "@mui/material";
import GatesLocationController from './GatesLocationController.js';
import GameSelector from '../Retail/components/GameSelector.js';
import GatesHeader from './components/GatesHeader.js';
import GatesOverview from './GatesOverview.js';
import GateCard from './components/GateCard.js';
import GateModal from './components/GateModal.js';
import GatesFlowChart from './charts/GatesFlowChart.js';
import OverviewInsights from '../Concession/components/OverviewInsights';
import { buildGatesInsights } from './gatesInsights';
import { USE_DUMMY_GATES_DATA } from './dummyLiveData';
import { ChartSegmented } from '../Concession/charts/chartTheme';
import { InfoTip, Panel, PanelHeader } from '../Concession/components/ConcessionUI';
import AnalysisWindow from '../Concession/components/AnalysisWindow';
import {
  buildGateStats, combinedCountSeries, recentRate, nowMinute, queueColor, minuteToClock, formatRate,
  intervalFlow, clockToMinute,
} from './gatesMetrics';
import '../Concession/concession.css';
import './gates.css';

// `text` for values that are words (a gate name) rather than numbers: smaller, and allowed to wrap
const Kpi = ({ label, value, accent, caption, info, text }) => (
  <div className="cx-kpi">
    <div className="cx-kpi-label">
      {label}
      {info && <InfoTip title={label} text={info} />}
    </div>
    <div className={`cx-kpi-value${text ? ' gx-kpi-text' : ''}`} style={accent ? { color: accent } : undefined}>{value}</div>
    {caption && <div className="cx-kpi-foot">{caption}</div>}
  </div>
);

// Headline numbers across every gate
const GatesKpis = ({ gateStats, combined, isLive, windowLabel }) => {
  const entrances = gateStats.reduce((sum, g) => sum + g.entrances, 0);
  const exits = gateStats.reduce((sum, g) => sum + g.exits, 0);
  const gateCount = windowLabel || `Across ${gateStats.length} gate${gateStats.length === 1 ? '' : 's'}`;

  if (isLive) {
    const withQueue = gateStats.filter((g) => g.queueNow);
    const longest = withQueue.reduce((top, g) => (!top || g.queueNow.value > top.queueNow.value ? g : top), null);
    const average = withQueue.length
      ? Math.round(withQueue.reduce((sum, g) => sum + g.queueNow.value, 0) / withQueue.length)
      : null;
    return (
      <div className="cx-kpi-grid">
        <Kpi
          label="Total entrances"
          value={entrances.toLocaleString()}
          caption={`${formatRate(recentRate(combined, 'in'))} / min now`}
          info="People who have entered through every gate today. The rate is the average over the last 5 minutes."
        />
        <Kpi
          label="Total exits"
          value={exits.toLocaleString()}
          caption={`${formatRate(recentRate(combined, 'out'))} / min now`}
          info="People who have left through every gate today. The rate is the average over the last 5 minutes."
        />
        <Kpi
          label="Longest queue"
          value={longest ? `${longest.queueNow.value} min` : '—'}
          accent={longest ? queueColor(longest.queueNow.value) : undefined}
          caption={longest ? longest.label : 'No recent queue readings'}
          info="The longest queue at any gate right now, from each gate's latest reading."
        />
        <Kpi
          label="Average queue"
          value={average != null ? `${average} min` : '—'}
          accent={average != null ? queueColor(average) : undefined}
          caption={withQueue.length ? `Across ${withQueue.length} gate${withQueue.length === 1 ? '' : 's'} with a reading` : 'No recent queue readings'}
          info="The average of each gate's latest queue reading. Gates without a recent reading are left out."
        />
      </div>
    );
  }

  const peak = gateStats.reduce((top, g) => (g.queuePeak && (!top || g.queuePeak.value > top.queuePeak.value) ? g : top), null);
  const busiest = gateStats.reduce((top, g) => (!top || g.entrances > top.entrances ? g : top), null);
  const busiestShare = busiest && entrances ? Math.round((busiest.entrances / entrances) * 100) : 0;
  return (
    <div className="cx-kpi-grid">
      <Kpi label="Total entrances" value={entrances.toLocaleString()} caption={gateCount} />
      <Kpi label="Total exits" value={exits.toLocaleString()} caption={gateCount} />
      <Kpi
        label="Peak queue"
        value={peak ? `${peak.queuePeak.value} min` : '—'}
        accent={peak ? queueColor(peak.queuePeak.value) : undefined}
        caption={peak ? `${peak.label} at ${minuteToClock(peak.queuePeak.minute)}` : 'No queue readings'}
        info="The longest queue reading at any gate in the analysis window."
      />
      <Kpi
        label="Busiest gate"
        text={!!(busiest && busiest.entrances)}
        value={busiest && busiest.entrances ? busiest.label : '—'}
        caption={busiest && busiest.entrances ? `${busiest.entrances.toLocaleString()} entrances · ${busiestShare}% of all` : 'No entrances recorded'}
      />
    </div>
  );
};

// Gate card order. Gates with no reading for the chosen measure go last; ties keep config order.
const sortGates = (gateStats, sortBy, isLive) => {
  if (sortBy === 'config') return gateStats;
  const value = (g) => {
    if (sortBy === 'entrances') return g.entrances;
    if (sortBy === 'exits') return g.exits;
    const queue = isLive ? g.queueNow : g.queuePeak;
    return queue ? queue.value : -1;
  };
  return gateStats
    .map((g, i) => ({ g, i }))
    .sort((a, b) => value(b.g) - value(a.g) || a.i - b.i)
    .map(({ g }) => g);
};

const GatesDashboard = ({ gatesData, currentGame, selectedDate, isToday, now, lastUpdated }) => {
  const [openStats, setOpenStats] = useState(null);
  const [sortBy, setSortBy] = useState('queue');
  const closeModal = useCallback(() => setOpenStats(null), []);

  const dateKey = moment(selectedDate).format('YYYY-MM-DD');
  const liveNow = isToday ? nowMinute(now) : null;

  const dayStats = useMemo(
    () => gatesData.map((gate) => buildGateStats(gate, dateKey, liveNow)),
    [gatesData, dateKey, liveNow]
  );
  const combined = useMemo(() => combinedCountSeries(dayStats.map((g) => g.counts)), [dayStats]);

  // Analysis window (past games, like concessions): minutes of the game day
  const dayStart = useMemo(() => moment(dateKey, 'YYYY-MM-DD').startOf('day').valueOf(), [dateKey]);
  const toTime = useCallback((minute) => new Date(dayStart + minute * 60000), [dayStart]);
  const dayRange = combined.length ? { start: combined[0].minute, end: combined[combined.length - 1].minute } : null;
  const [picked, setPicked] = useState(null);
  const handleRangeChange = useCallback((from, to) => {
    setPicked({ start: Math.round((from.getTime() - dayStart) / 60000), end: Math.round((to.getTime() - dayStart) / 60000) });
  }, [dayStart]);
  const activeWindow = !isToday && picked && dayRange && (picked.start > dayRange.start || picked.end < dayRange.end)
    ? picked
    : null;
  const activity = useMemo(
    () => intervalFlow(combined, 1).map((b) => ({ t: toTime(b.start).getTime(), y: b.in })),
    [combined, toTime]
  );
  const gatesOpenAt = clockToMinute(currentGame?.gatesOpen);
  const gameStartAt = clockToMinute(currentGame?.gameStartTime);

  // With a window picked, every number follows it; otherwise the day's totals
  const gateStats = useMemo(
    () => (activeWindow ? gatesData.map((gate) => buildGateStats(gate, dateKey, liveNow, activeWindow)) : dayStats),
    [activeWindow, gatesData, dateKey, liveNow, dayStats]
  );
  const totalEntrances = gateStats.reduce((sum, g) => sum + g.entrances, 0);
  const totalExits = gateStats.reduce((sum, g) => sum + g.exits, 0);
  const insights = useMemo(
    () => buildGatesInsights({ gateStats: dayStats, range: activeWindow, currentGame, isLive: isToday }),
    [dayStats, activeWindow, currentGame, isToday]
  );
  const windowLabel = activeWindow ? `${minuteToClock(activeWindow.start)} – ${minuteToClock(activeWindow.end)}` : null;

  // Keep the open modal in step with refreshed data
  const modalStats = openStats ? gateStats.find((g) => g.gate.id === openStats.gate.id) || openStats : null;

  if (!gatesData.length) {
    return (
      <div className="cx-empty-state">
        <div className="cx-empty-title">No gate data for this game</div>
        <div>Pick another game above, or check back once the gates open.</div>
      </div>
    );
  }

  return (
    <>
      {!isToday && dayRange && (
        <div className="mb-4">
          <Panel>
            <PanelHeader
              title="Analysis window"
              subtitle="Pick a preset or drag the handles. Every number and chart below follows this range."
            />
            <AnalysisWindow
              earliestTime={toTime(dayRange.start)}
              latestTime={toTime(dayRange.end)}
              onRangeChange={handleRangeChange}
              activity={activity}
              gameStart={gameStartAt != null ? toTime(gameStartAt).getTime() : undefined}
              gatesOpen={gatesOpenAt != null ? toTime(gatesOpenAt).getTime() : undefined}
            />
          </Panel>
        </div>
      )}

      <GatesKpis gateStats={gateStats} combined={combined} isLive={isToday} windowLabel={windowLabel} />

      {insights.length > 0 && (
        <div className="mb-4">
          <Panel>
            <PanelHeader
              title="What stands out"
              subtitle={isToday
                ? 'Findings from today so far, most important first. They update with the live data every minute.'
                : `Findings ${windowLabel ? `for ${windowLabel}` : 'from this game'}, most important first.`}
              actions={isToday && lastUpdated ? (
                <span className="cx-updated gx-updated">
                  <span className="pulsing-dot" />
                  Updated {moment(lastUpdated).format('h:mm A')}
                </span>
              ) : undefined}
            />
            <OverviewInsights insights={insights} />
          </Panel>
        </div>
      )}

      <div className="gx-section-head">
        <div className="gx-section-title">
          <h2 className="cx-panel-title">Gates</h2>
          <span className="gx-section-note">
            {isToday ? 'Live queue and counts for each gate.' : `Peak queue and counts for each gate${windowLabel ? `, ${windowLabel}` : ''}.`} Select a gate for its charts.
          </span>
        </div>
        <ChartSegmented
          label="Sort by"
          options={[
            { value: 'queue', label: isToday ? 'Queue now' : 'Peak queue' },
            { value: 'entrances', label: 'Entrances' },
            { value: 'exits', label: 'Exits' },
            { value: 'config', label: 'Gate order' },
          ]}
          value={sortBy}
          onChange={setSortBy}
        />
      </div>
      <div className="gx-gcard-grid">
        {sortGates(gateStats, sortBy, isToday).map((stats) => (
          <GateCard
            key={stats.gate.id}
            stats={stats}
            isLive={isToday}
            entryShare={totalEntrances ? (stats.entrances / totalEntrances) * 100 : 0}
            exitShare={totalExits ? (stats.exits / totalExits) * 100 : 0}
            toTime={toTime}
            range={activeWindow}
            gatesOpen={gatesOpenAt}
            gameStart={gameStartAt}
            onOpen={setOpenStats}
          />
        ))}
      </div>

      <GatesFlowChart
        series={combined}
        currentGame={currentGame}
        range={activeWindow}
        who="all gates"
      />

      <GateModal stats={modalStats} isLive={isToday} currentGame={currentGame} range={activeWindow} onClose={closeModal} />
    </>
  );
};

const GatesSkeleton = () => (
  <div className="cx-content" aria-busy="true" aria-label="Loading">
    <div className="cx-kpi-grid">
      {[0, 1, 2, 3].map((i) => <div key={i} className="cx-skel" style={{ height: 118, borderRadius: 20 }} />)}
    </div>
    <div className="gx-gcard-grid">
      {[0, 1, 2, 3].map((i) => <div key={i} className="cx-skel" style={{ height: 230, borderRadius: 20 }} />)}
    </div>
  </div>
);

const GatesLocation = () => {
  // Overview (compare games) or Game Day, like concessions and retail
  const [dayType, setDayType] = useState('game');
  return (
    <GatesLocationController>
      {({
        loading,
        backgroundLoading,
        currentLocation,
        games,
        now,
        currentSeries,
        currentGame,
        currentSeason,
        isToday,
        selectedDate,
        gatesData,
        lastUpdated,
        autoFetchEnabled,
        setCurrentSeason,
        setCurrentGame,
        setCurrentSeries,
        setSelectedDate,
        authCtx
      }) => (
        <>
          {authCtx.loggedIn ? (
            <div className="cx-page">
              {/* TEMPORARY: remove with dummyLiveData.js */}
              {USE_DUMMY_GATES_DATA && (
                <div style={{ background: '#d97706', color: '#fff', textAlign: 'center', padding: '4px', fontWeight: 600 }}>
                  Dummy data mode: schedule and gate data are fake (local dev only)
                </div>
              )}
              <div className="sticky-top cx-topbar">
                <GatesHeader
                  currentLocation={currentLocation}
                  isToday={isToday}
                  selectedDate={selectedDate}
                  dayType={dayType}
                  setDayType={setDayType}
                />
                {dayType === 'game' && (
                <GameSelector
                  loading={loading}
                  currentLocation={currentLocation}
                  currentSeries={currentSeries}
                  currentGame={currentGame}
                  currentSeason={currentSeason}
                  setCurrentSeason={setCurrentSeason}
                  games={games}
                  now={now}
                  isToday={isToday}
                  setCurrentGame={setCurrentGame}
                  setCurrentSeries={setCurrentSeries}
                  setSelectedDate={setSelectedDate}
                />
                )}

                {/* Thin bar while live data refreshes in the background */}
                {dayType === 'game' && backgroundLoading && autoFetchEnabled && (
                  <div style={{ position: "fixed", top: 0, left: 0, right: 0, width: "100%", zIndex: 9999 }}>
                    <LinearProgress
                      sx={{
                        backgroundColor: "rgba(2, 166, 147, 0.25)",
                        height: 8,
                        "& .MuiLinearProgress-bar": { backgroundColor: "rgb(2, 166, 147)" },
                      }}
                    />
                  </div>
                )}
              </div>

              {dayType === 'overview' ? (
                <div className="cx-content">
                  <GatesOverview currentLocation={currentLocation} games={games} />
                </div>
              ) : loading && !backgroundLoading ? (
                <GatesSkeleton />
              ) : (
                <div className="cx-content">
                  <GatesDashboard
                    gatesData={gatesData}
                    currentGame={currentGame}
                    selectedDate={selectedDate}
                    isToday={isToday}
                    now={now}
                    lastUpdated={lastUpdated}
                  />
                </div>
              )}
            </div>
          ) : (
            <div className="cx-unauthorized">
              <Link to={"/auth"}>
                Not Authorized. Please Log-In with given credentials or contact support.
              </Link>
            </div>
          )}
        </>
      )}
    </GatesLocationController>
  );
};

export default GatesLocation;
